import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession, fetchUserAttributes, signIn } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import awsConfig from './aws-config';
import Dashboard from './components/Dashboard';

Amplify.configure(awsConfig);

interface User {
  username: string;
  email?: string;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          await checkUser();
          break;
        case 'signInWithRedirect_failure':
          setError('Sign in failed. Please try again.');
          setLoading(false);
          break;
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    checkUser();
    return () => unsubscribe();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      let email = currentUser.username;
      try {
        const attributes = await fetchUserAttributes();
        email = attributes.email || currentUser.username;
      } catch {
        try {
          const session = await fetchAuthSession();
          email = (session.tokens?.idToken?.payload?.email as string) || currentUser.username;
        } catch {}
      }
      setUser({ username: currentUser.username, email });
      setError(null);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    try {
      setLoading(true);
      setError(null);
      await signInWithRedirect({ provider: 'Google' });
    } catch (error: any) {
      setError(error.message || 'Failed to sign in');
      setLoading(false);
    }
  }

  async function handlePasswordSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoggingIn(true);
    setError(null);
    try {
      const result = await signIn({ username: email, password });
      if (result.isSignedIn) {
        await checkUser();
      } else {
        setError('Additional authentication required.');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to sign in');
    }
    setLoggingIn(false);
  }

  async function handleSignOut() {
    try {
      await signOut({ global: true });
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-arbitium-darker flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-arbitium-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 font-nunito">Loading Arbitium...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-arbitium-darker flex items-center justify-center overflow-hidden font-nunito">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-arbitium-darker via-arbitium-dark to-arbitium-darker"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-arbitium-primary/30 rounded-full blur-3xl pulse-glow"></div>
          <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-arbitium-accent/20 rounded-full blur-3xl pulse-glow" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-arbitium-secondary/10 rounded-full blur-3xl pulse-glow" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative z-10 bg-arbitium-dark/70 backdrop-blur-2xl border border-arbitium-primary/20 rounded-3xl p-12 max-w-md w-full mx-4 text-center shadow-2xl shadow-arbitium-primary/10">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-arbitium-primary to-arbitium-accent rounded-2xl blur-xl opacity-50"></div>
              <div className="relative w-full h-full bg-arbitium-darker rounded-2xl flex items-center justify-center border border-arbitium-primary/30">
                <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
                  <defs>
                    <linearGradient id="logoGradient" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" style={{stopColor:'#06B6D4'}}/>
                      <stop offset="50%" style={{stopColor:'#8B5CF6'}}/>
                      <stop offset="100%" style={{stopColor:'#A78BFA'}}/>
                    </linearGradient>
                  </defs>
                  <path d="M30 6L9 54H16.5L21 42H39L43.5 54H51L30 6Z" stroke="url(#logoGradient)" strokeWidth="2.5" fill="none"/>
                  <path d="M24 36L30 18L36 36" stroke="url(#logoGradient)" strokeWidth="2.5" fill="none"/>
                  <line x1="18" y1="48" x2="42" y2="48" stroke="url(#logoGradient)" strokeWidth="1.5"/>
                  <line x1="21" y1="42" x2="39" y2="42" stroke="url(#logoGradient)" strokeWidth="1.5"/>
                  <line x1="24" y1="36" x2="36" y2="36" stroke="url(#logoGradient)" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold gradient-text mb-2">arbitium</h1>
            <p className="text-gray-400">Autonomous Security Platform</p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {!showPasswordLogin ? (
            <>
              <button
                onClick={handleGoogleSignIn}
                className="w-full bg-white text-gray-800 font-semibold py-3.5 px-6 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-100 transition-all mb-4 shadow-lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-arbitium-primary/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-arbitium-dark text-gray-500">or</span>
                </div>
              </div>
              
              <button
                onClick={() => setShowPasswordLogin(true)}
                className="w-full bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white font-semibold py-3.5 px-6 rounded-xl hover:opacity-90 transition-all shadow-lg shadow-arbitium-primary/30"
              >
                Sign in with Email
              </button>
            </>
          ) : (
            <form onSubmit={handlePasswordSignIn}>
              <div className="mb-4">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-arbitium-darker border border-arbitium-primary/30 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-arbitium-primary transition-colors"
                  required
                />
              </div>
              <div className="mb-6">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-arbitium-darker border border-arbitium-primary/30 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-arbitium-primary transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white font-semibold py-3.5 px-6 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 mb-4 shadow-lg shadow-arbitium-primary/30"
              >
                {loggingIn ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordLogin(false)}
                className="w-full text-gray-400 hover:text-white transition-colors"
              >
                ← Back to options
              </button>
            </form>
          )}
          
          <p className="text-gray-500 text-sm mt-6">
            Authorized users only
          </p>
        </div>
      </div>
    );
  }

  return <Dashboard user={user} onSignOut={handleSignOut} />;
}

export default App;
